/**
 * @file     sellerFlow.ts
 * @readme   ../README.md
 * @purpose  Seller (home value / "I'm Selling") flow configuration: home details, address, (optional free valuation tick), then name, email and mobile that file the lead, then the starting range at once with "Get comparable solds", which opens the dashboard Home (VOW opt-in, then the analysis) once the email is confirmed. Also the Fast Track and /l landing variants, which share the steps and keep their own source attribution.
 */

import { ownsPropertyTag } from '@/services/leads/ownsProperty';
import type { FlowConfig } from '../types';
import { CALL_REQUEST_ANSWER_KEY, CALL_REQUESTED_TAG } from '../vowOptIn';

export const SELLER_FLOW_CONFIG: FlowConfig = {
  flowId: 'value_pulse',
  slug: '/home-value',
  leadType: 'seller',
  draftVersion: 4,
  steps: [
    {
      id: 'entry',
      type: 'entry',
      eyebrowKey: 'homeValueFlow.entry.eyebrow',
      eyebrowDefault: 'For homeowners',
      headlineKey: 'homeValueFlow.entry.title',
      headlineDefault: 'See a starting range for your home',
      benefits: [
        {
          glyph: 'chart',
          textKey: 'homeValueFlow.entry.benefitRange',
          textDefault: 'See asking prices for similar homes for sale nearby.',
        },
      ],
      ctaKey: 'homeValueFlow.entry.cta',
      ctaDefault: 'Start your valuation',
    },
    {
      id: 'details',
      type: 'pills',
      headlineKey: 'homeValueFlow.details.title',
      headlineDefault: 'About the home',
      ctaKey: 'homeValueFlow.details.cta',
      ctaDefault: 'Continue',
      valuationTick: true,
      pillGroups: [
        {
          answerKey: 'propertyType',
          labelKey: 'homeValueFlow.propertyType.title',
          labelDefault: 'Property type',
          required: true,
          options: [
            { value: 'detached', labelKey: 'homeValueFlow.propertyType.detached', labelDefault: 'Detached' },
            { value: 'condo', labelKey: 'homeValueFlow.propertyType.condo', labelDefault: 'Condo' },
            { value: 'townhouse', labelKey: 'homeValueFlow.propertyType.townhouse', labelDefault: 'Townhouse' },
            { value: 'other', labelKey: 'homeValueFlow.propertyType.other', labelDefault: 'Other' },
          ],
        },
        {
          answerKey: 'beds',
          labelKey: 'homeValueFlow.beds.label',
          labelDefault: 'Bedrooms',
          required: true,
          groupIcon: 'bed',
          options: [
            { value: '0', labelKey: 'homeValueFlow.beds.studio', labelDefault: 'Studio' },
            { value: '1', labelKey: 'homeValueFlow.beds.one', labelDefault: '1' },
            { value: '2', labelKey: 'homeValueFlow.beds.two', labelDefault: '2' },
            { value: '3', labelKey: 'homeValueFlow.beds.three', labelDefault: '3' },
            { value: '4', labelKey: 'homeValueFlow.beds.four', labelDefault: '4' },
            { value: '5', labelKey: 'homeValueFlow.beds.five', labelDefault: '5 or more' },
          ],
        },
        {
          answerKey: 'baths',
          labelKey: 'homeValueFlow.baths.label',
          labelDefault: 'Bathrooms',
          required: true,
          groupIcon: 'bath',
          options: [
            { value: '1', labelKey: 'homeValueFlow.baths.one', labelDefault: '1' },
            { value: '2', labelKey: 'homeValueFlow.baths.two', labelDefault: '2' },
            { value: '3', labelKey: 'homeValueFlow.baths.three', labelDefault: '3' },
            { value: '4', labelKey: 'homeValueFlow.baths.four', labelDefault: '4 or more' },
          ],
        },
      ],
    },
    {
      id: 'address',
      type: 'textInput',
      headlineKey: 'homeValueFlow.address.title',
      headlineDefault: 'Where is the home?',
      fieldLabelKey: 'homeValueFlow.address.label',
      fieldLabelDefault: 'Property address',
      placeholderKey: 'homeValueFlow.address.placeholder',
      placeholderDefault: 'Start typing the property address',
      minLength: 6,
      addressAutocomplete: true,
      unitField: true,
      unitFieldLabelKey: 'homeValueFlow.address.unitLabel',
      unitFieldLabelDefault: 'Unit or suite',
      unitPlaceholderKey: 'homeValueFlow.address.unitPlaceholder',
      unitPlaceholderDefault: 'Unit 402',
      provinceField: true,
      pills: {
        answerKey: 'ownsProperty',
        labelKey: 'homeValueFlow.ownsProperty.label',
        labelDefault: 'Do you own this property?',
        required: true,
        options: [
          { value: 'yes', labelKey: 'homeValueFlow.ownsProperty.yes', labelDefault: 'Yes' },
          { value: 'no', labelKey: 'homeValueFlow.ownsProperty.no', labelDefault: 'No' },
        ],
      },
    },
    {
      id: 'capture',
      type: 'capture',
      headlineKey: 'homeValueFlow.capture.rangeTitle',
      headlineDefault: 'Get your starting range',
      ctaKey: 'homeValueFlow.capture.rangeSubmit',
      ctaDefault: 'See my range',
      // Submitting files the seller lead and texts a code to the mobile; the
      // range shows only once the code (or, as the fallback, the email link)
      // confirms the contact (Ray, 2026-10-29). The mobile taken here also
      // prefills the VOW opt-in on the dashboard Home.
      captureFields: ['firstName', 'lastName', 'email', 'phone'],
      requiredCaptureFields: ['firstName', 'lastName', 'email', 'phone'],
      accountCapture: true,
      // Two ticks above the button were losing sellers at the last step (Ray,
      // 2026-10). Text consent cannot be a passive line, so this form asks for none.
      hideSmsOptIn: true,
      verifyBeforeAdvance: true,
      verifyPendingHeadlineKey: 'homeValueFlow.capture.rangeVerifyHeadline',
      verifyPendingHeadlineDefault: 'Confirm your email to see your range',
      verifyPendingBodyKey: 'homeValueFlow.capture.rangeVerifyBody',
      verifyPendingBodyDefault: 'Open the link sent to {{email}}. Your range opens here as soon as you do.',
    },
    {
      id: 'estimate',
      type: 'valueRange',
      terminal: true,
      headlineKey: 'homeValueFlow.estimate.previewTitle',
      headlineDefault: 'Your starting range',
      ctaKey: 'homeValueFlow.estimate.soldsCta',
      ctaDefault: 'Get comparable solds',
    },
  ],
  analytics: {
    start: 'lead_flow_started',
    stepCompleted: 'lead_flow_step_completed',
  },
  buildTags: (answers) => {
    const tags = ['value_pulse', 'seller_valuation'];
    if (answers.propertyType) tags.push(`Property type: ${answers.propertyType}`);
    if (answers.beds) tags.push(`Beds: ${answers.beds}`);
    if (answers.baths) tags.push(`Baths: ${answers.baths}`);
    const ownership = ownsPropertyTag(answers.ownsProperty);
    if (ownership) tags.push(ownership);
    if (answers[CALL_REQUEST_ANSWER_KEY] === 'yes') tags.push(CALL_REQUESTED_TAG);
    return tags;
  },
};

/**
 * The sell-for-more Fast Track guide request, folded into the seller flow. It
 * keeps its pending source (`fast_track`) so the verify route still mails the
 * guide, and its own flow id and tags for attribution.
 */
export const SELLER_FAST_TRACK_FLOW_CONFIG: FlowConfig = {
  ...SELLER_FLOW_CONFIG,
  flowId: 'fast_track',
  slug: '/sell-for-more',
  pendingFlowType: 'fast_track',
  buildTags: (answers) => [
    'fast_track',
    'seller_guide',
    ...(SELLER_FLOW_CONFIG.buildTags?.(answers) ?? []).filter(
      (tag) => tag !== 'value_pulse' && tag !== 'seller_valuation',
    ),
  ],
};

/** /l seller landing pages: the same flow, keeping the express form's flow id. */
export const SELLER_LANDING_FLOW_CONFIG: FlowConfig = {
  ...SELLER_FLOW_CONFIG,
  flowId: 'landing-express',
  slug: '/l',
};
